import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { useUpdateNotificationByIds } from "@hooks/useUpdateNotificationByIds";
import { Pill } from "./Pill";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu";
import { useNotificationsContext } from "@hooks/useNotificationsContext";
import { IconButton } from "./IconButton";
import {
  Eye,
  EyeOff,
  Trash01,
  DotsVertical,
  ArrowUpRight,
} from "untitledui-js/react";
import { Label } from "@components/Label";
import { Alert } from "@components/Alert";
import { NotificationDetails } from "./NotificationDetails";
import { Notification } from "@hooks/useGetNotifications";
import { useConfigContext } from "@hooks/useConfigContext";
import { showNotificationTimeSince } from "@utils/showNotificationTimeSince";

const ICON_SIZE = 14;

export const Notifications = () => {
  const { notifications = [], hasUnreadNotifications = false } =
    useNotificationsContext();
  const { t } = useTranslation();
  const { mutate: updateNotificationByIds } = useUpdateNotificationByIds();

  // local state
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [toggleAllRead, setToggleAllRead] = useState<boolean>(
    hasUnreadNotifications
  );
  const [notificationDetails, setNotificationDetails] =
    useState<Notification | null>(null);

  // derived state
  const unreadNotificationCount = notifications.filter(
    (notification) => !notification.isRead
  ).length;
  const hasNotifications = notifications.length > 0;

  return (
    <div
      className={twMerge("custom-scrollbar max-h-[70vh] overflow-y-auto pr-2")}
    >
      {notificationDetails ? (
        <NotificationDetails
          notificationDetails={notificationDetails}
          setNotificationDetails={setNotificationDetails}
        />
      ) : (
        <>
          <div
            className={twMerge(
              "mb-4 flex items-center justify-between gap-4",
              hasNotifications ? "md:min-w-[500px]" : "md:min-w-56"
            )}
          >
            <div className="flex items-start gap-2">
              <Typography as="h6" className="text-lg font-bold text-white">
                {t("notifications.title")}
              </Typography>
              {hasUnreadNotifications && (
                <Pill variant="success">
                  {t("notifications.count", { count: unreadNotificationCount })}
                </Pill>
              )}
            </div>
            {hasNotifications && (
              <div className="relative">
                <IconButton
                  id="notificationMenu"
                  active={false}
                  showAlert={false}
                  ariaLabel={t("notifications.markAllAsRead")}
                  icon={<DotsVertical width={ICON_SIZE} />}
                  className="h-6 w-6 *:h-2.5 *:w-2.5"
                  onClick={() => setIsOpen((prev) => !prev)}
                />
                <DropdownMenu
                  id="notification-dropdown"
                  isOpen={isOpen}
                  variant="inverted"
                >
                  <DropdownMenuItem
                    onClick={() => {
                      setToggleAllRead((prev) => !prev);
                      updateNotificationByIds(
                        notifications.map((notification) => ({
                          id: notification.id,
                          isDeleted: false,
                          isRead: toggleAllRead,
                        }))
                      );
                    }}
                    variant="inverted"
                    size="sm"
                  >
                    {toggleAllRead ? (
                      <>
                        <Eye width={ICON_SIZE} />
                        {t("notifications.markAllAsRead")}
                      </>
                    ) : (
                      <>
                        <EyeOff width={ICON_SIZE} />
                        {t("notifications.markAllAsUnread")}
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      updateNotificationByIds(
                        notifications.map((notification) => ({
                          id: notification.id,
                          isDeleted: true,
                          isRead: true,
                        }))
                      );
                    }}
                    variant="inverted"
                    size="sm"
                  >
                    <Trash01 width={ICON_SIZE} />
                    {t("notifications.markAllAsDeleted")}
                  </DropdownMenuItem>
                </DropdownMenu>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-1">
            {/* Messaging */}
            {hasNotifications === false ? (
              <Alert
                labelText={t("notifications.done")}
                text={t("notifications.noNotifications")}
                className="w-full max-w-none"
                textClassName="text-tertiary-100"
              />
            ) : (
              notifications.map((notification) => {
                const notificationTimeSince = showNotificationTimeSince(
                  notification.createdAt,
                  t
                );
                return (
                  <div
                    key={notification.id}
                    className={twMerge(
                      "group relative z-0 flex justify-between gap-2 rounded-2xl border border-white/10 p-3 pl-12",
                      "hover:border-primary-600",
                      // transition border color
                      "transition-border-color duration-300",
                      "after:absolute after:top-1/2 after:left-4 after:z-10 after:block after:h-4 after:w-4 after:-translate-y-1/2 after:rounded-full after:bg-accent_one-100",
                      notification.isRead
                        ? "after:bg-white/10"
                        : "after:bg-accent_two-500"
                    )}
                  >
                    <div className="relative flex flex-col gap-1">
                      <NotificationTitle
                        notification={notification}
                        setNotificationDetails={setNotificationDetails}
                        updateNotificationByIds={updateNotificationByIds}
                      />
                      <Typography className="text-xs font-normal text-white/80">
                        {notification.description}
                      </Typography>
                      <Typography className="text-xs font-semibold text-white/80">
                        {notificationTimeSince}
                      </Typography>
                    </div>
                    <div className="flex flex-col items-end justify-between gap-4">
                      <div className="flex gap-2">
                        <IconButton
                          id="markAsRead"
                          onClick={() => {
                            updateNotificationByIds([
                              {
                                id: notification.id,
                                isDeleted: false,
                                isRead: !notification.isRead,
                              },
                            ]);
                          }}
                          ariaLabel={t("notifications.markAsRead")}
                          icon={
                            notification.isRead ? (
                              <EyeOff width={ICON_SIZE} />
                            ) : (
                              <Eye width={ICON_SIZE} />
                            )
                          }
                          variant="sm"
                        />
                        <IconButton
                          id="markAsDeleted"
                          onClick={() => {
                            updateNotificationByIds([
                              {
                                id: notification.id,
                                isDeleted: true,
                                isRead: true,
                              },
                            ]);
                          }}
                          ariaLabel={t("notifications.markAsDeleted")}
                          icon={<Trash01 width={ICON_SIZE} />}
                          variant="sm"
                        />
                        {notification.link && (
                          <IconButton
                            className="group-hover:bg-primary-400 hover:bg-primary-400"
                            id="navIndicator"
                            onClick={() => {}}
                            icon={<ArrowUpRight width={ICON_SIZE} />}
                            ariaLabel={t("notifications.view")}
                            shouldRotate={true}
                            variant="sm"
                          />
                        )}
                      </div>
                      {notification.category && (
                        <Label variant="soft">{notification.category}</Label>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface NotificationTitleProps {
  notification: Notification;
  setNotificationDetails: (notification: Notification) => void;
  updateNotificationByIds: (
    notifications: {
      id: string;
      isDeleted: boolean;
      isRead: boolean;
    }[]
  ) => void;
}

const NotificationTitle = ({
  notification,
  setNotificationDetails,
  updateNotificationByIds,
}: NotificationTitleProps) => {
  const { dispatch } = useConfigContext();

  if (notification.body && notification?.body.length > 0) {
    return (
      <button
        onClick={() => setNotificationDetails(notification)}
        className="cursor-pointer text-left text-sm font-bold text-white"
      >
        {notification.title}
        <span className="absolute inset-0"></span>
      </button>
    );
  }

  if (notification.link) {
    return (
      <Link
        to={`/${notification.link}`}
        onClick={() => {
          // Set the notification as read.
          updateNotificationByIds([
            {
              id: notification.id,
              isDeleted: false,
              isRead: true,
            },
          ]);
          // Close the notifications panel.
          dispatch({
            type: "NOTIFICATIONS_VISIBILITY",
            payload: { visible: false },
          });
        }}
      >
        <Typography className="text-sm font-bold text-white">
          {notification.title}
          <span className="absolute inset-0"></span>
        </Typography>
      </Link>
    );
  }

  return (
    <Typography className="text-sm font-bold text-white">
      {notification.title}
      <span className="absolute inset-0"></span>
    </Typography>
  );
};
